/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */
import { EMPTY, Observable, of } from 'rxjs';
import { scan, switchMap } from 'rxjs/operators';
import {
	FancyTicketAction,
	TicketActionTypes,
	ticketRemoveActions,
} from 'src/app/app-data/actions/ticket-actions';
import { isActionIncluded } from 'src/app/common/fancy-firebase/util/is-action-included';
import { TicketRenderTypes } from '@touchplan/common';

/**
 * A helper function that accepts an observable of [[FancyTicketAction]]'s and returns a new observable
 * which filters it down to a set that only emits based on the edit renderType. Generates a special new event
 * when an item leaves the edit list.
 * Typically will be used like:
 * `const editOb$ = makeTicketEditObservable(ticketList.rawEvent$);`
 */
export function makeTicketEditObservable(
	tickets: Observable<FancyTicketAction>,
	renderType = TicketRenderTypes.edit
) {
	return tickets.pipe(
		//use the scan to emit a pseudo delete event
		scan(
			(acc, action) => {
				//if edit, set in cache
				if (action.payload.renderType === renderType) {
					acc.list.set(action.key, true);
				}
				//if not, remove from cache
				else if (acc.list.has(action.key)) {
					acc.list.delete(action.key);
					action = new FancyTicketAction(action.payload, TicketActionTypes.deleteTicket);
				}

				return { list: acc.list, action };
			},
			{ list: new Map<string, boolean>(), action: null }
		),
		// tap(action => logger.log('group input', action)),
		switchMap((actionGroup) => {
			const action = actionGroup.action;
			if (action.payload.renderType === renderType || isActionIncluded(action, ticketRemoveActions)) {
				return of(action);
			}
			return EMPTY;
		})
	);

	//editTicketsWithUpdate$ on normal ticketList will not work as it doesn't emit inital events (delibrately according to the comments)
	// return tickets.editTicketsWithUpdate$.pipe(tap( editTickets => logger.log('group, editTickets emission', editTickets)));
}
